import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { chatbotApi, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "../components/ui/alert-dialog";
import { ArrowLeft, Upload, Link as LinkIcon, FileText, MessageSquare, Trash2, Loader2, Play, Plus, AlertTriangle } from "lucide-react";
import { PageHeader, SectionCard } from "../components/ui/page-shell";
import { toast } from "sonner";

export default function ChatbotTraining() {
  const { id } = useParams();
  const [chatbot, setChatbot] = useState(null);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [training, setTraining] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const [textInput, setTextInput] = useState("");
  const [qaOpen, setQaOpen] = useState(false);
  const [qaQuestion, setQaQuestion] = useState("");
  const [qaAnswer, setQaAnswer] = useState("");
  const [activeTab, setActiveTab] = useState("files");
  const [redactTarget, setRedactTarget] = useState(null);
  const [redacting, setRedacting] = useState(false);
  const TYPE_FOR_TAB = { files: "file", urls: "url", text: "text", qa: "qa" };
  const LABEL_FOR_TAB = { files: "Files", urls: "URLs", text: "Text", qa: "Q&A Pairs" };

  // BC-009: personal data counts reported by the backend per training source
  const piiCounts = (source) => {
    const counts = source?.metadata?.pii_counts;
    if (!counts || typeof counts !== "object") return null;
    const entries = Object.entries(counts).filter(([, n]) => Number(n) > 0);
    if (entries.length === 0) return null;
    return entries;
  };
  const piiTotal = (source) => {
    const entries = piiCounts(source);
    return entries ? entries.reduce((sum, [, n]) => sum + Number(n), 0) : 0;
  };
  const PII_LABELS = { IC: "IC number", PHONE: "phone number", EMAIL: "email address", CARD: "card number", BANK: "bank account number", PASSPORT: "passport number" };
  const piiSummary = (source) => {
    const entries = piiCounts(source);
    if (!entries) return "";
    return entries
      .map(([type, n]) => {
        const label = PII_LABELS[type] || type.toLowerCase();
        return `${n} ${label}${Number(n) === 1 ? "" : "s"}`;
      })
      .join(", ");
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [chatbotRes, sourcesRes] = await Promise.all([
        chatbotApi.get(id),
        chatbotApi.getSources(id)
      ]);
      setChatbot(chatbotRes.data);
      setSources(sourcesRes.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const res = await chatbotApi.uploadFile(id, file);
      setSources([res.data, ...sources]);
      toast.success(`File "${file.name}" uploaded successfully`);
      if (piiTotal(res.data) > 0) toast.info("This source contains personal data. You can mask it below.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleAddUrl = async () => {
    if (!urlInput.trim()) return;
    try {
      const res = await chatbotApi.addSource(id, { type: "url", content: urlInput.trim() });
      setSources([res.data, ...sources]);
      setUrlInput("");
      toast.success("URL added successfully");
      if (piiTotal(res.data) > 0) toast.info("This source contains personal data. You can mask it below.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleAddText = async () => {
    if (!textInput.trim()) return;
    try {
      const res = await chatbotApi.addSource(id, { type: "text", content: textInput.trim() });
      setSources([res.data, ...sources]);
      setTextInput("");
      toast.success("Text added successfully");
      if (piiTotal(res.data) > 0) toast.info("This source contains personal data. You can mask it below.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleAddQA = async () => {
    if (!qaQuestion.trim() || !qaAnswer.trim()) return;
    try {
      const res = await chatbotApi.addSource(id, {
        type: "qa",
        content: `Q: ${qaQuestion}\nA: ${qaAnswer}`,
        metadata: { question: qaQuestion, answer: qaAnswer }
      });
      setSources([res.data, ...sources]);
      setQaQuestion("");
      setQaAnswer("");
      setQaOpen(false);
      toast.success("Q&A added successfully");
      if (piiTotal(res.data) > 0) toast.info("This source contains personal data. You can mask it below.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // BC-009: mask personal data (IC, phone, email, ...) inside a training source
  const handleRedactSource = async () => {
    if (!redactTarget) return;
    setRedacting(true);
    try {
      const res = await chatbotApi.redactSourcePii(id, redactTarget.id);
      setSources(sources.map(s => (s.id === res.data.source.id ? res.data.source : s)));
      const n = Object.values(res.data.redacted || {}).reduce((a, b) => a + Number(b), 0);
      if (n > 0) toast.success("Personal data masked successfully");
      else toast.info("Nothing left to mask. Numbers on your privacy allowlist are kept on purpose.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setRedacting(false);
      setRedactTarget(null);
    }
  };

  const handleDeleteSource = async (sourceId) => {
    if (!window.confirm("Delete this training source?")) return;
    try {
      await chatbotApi.deleteSource(id, sourceId);
      setSources(sources.filter(s => s.id !== sourceId));
      toast.success("Source deleted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleTrain = async () => {
    if (sources.length === 0) {
      toast.error("Add at least one training source first");
      return;
    }
    setTraining(true);
    try {
      await chatbotApi.train(id);
      toast.success("Training started!");
      const interval = setInterval(async () => {
        const res = await chatbotApi.getStatus(id);
        setChatbot(prev => ({ ...prev, status: res.data.status }));
        if (res.data.status === "live" || res.data.status === "failed") {
          clearInterval(interval);
          setTraining(false);
          if (res.data.status === "live") {
            toast.success("Training complete!");
            // Refresh sources so personal data found in fetched pages shows up.
            loadData();
          } else {
            toast.error("Training failed");
          }
        }
      }, 2000);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setTraining(false);
    }
  };

  const getSourceIcon = (type) => {
    switch (type) {
      case "file": return <FileText className="w-4 h-4" />;
      case "url": return <LinkIcon className="w-4 h-4" />;
      case "qa": return <MessageSquare className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="chatbot-training">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to={`/chatbot/${id}`}>
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <ArrowLeft className="w-4 h-4" />
          </Button>
        </Link>
        <div className="flex-1">
          <PageHeader title="Training Data" subtitle="Upload documents, sync URLs, and manage your knowledge base." />
          <p className="text-muted-foreground">{chatbot?.name}</p>
        </div>
        <Button onClick={handleTrain} disabled={training || sources.length === 0} data-testid="start-training-btn">
          {training ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
          {training ? "Training..." : "Start Training"}
        </Button>
      </div>

      {/* Add Sources */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full max-w-lg grid-cols-4 rounded-none">
          <TabsTrigger value="files" className="rounded-none">Files</TabsTrigger>
          <TabsTrigger value="urls" className="rounded-none">URLs</TabsTrigger>
          <TabsTrigger value="text" className="rounded-none">Text</TabsTrigger>
          <TabsTrigger value="qa" className="rounded-none">Q&A</TabsTrigger>
        </TabsList>

        <TabsContent value="files">
          <div className="border border-dashed border-border p-8 text-center">
            <Upload className="w-10 h-10 mx-auto text-muted-foreground mb-4" />
            <p className="font-medium mb-2">Upload Documents</p>
            <p className="text-sm text-muted-foreground mb-4">PDF, DOCX, TXT, or CSV files</p>
            <label>
              <input
                type="file"
                accept=".pdf,.docx,.txt,.csv"
                onChange={handleFileUpload}
                className="hidden"
                disabled={uploading}
              />
              <Button variant="outline" disabled={uploading} asChild>
                <span data-testid="upload-file-btn">
                  {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  {uploading ? "Uploading..." : "Choose File"}
                </span>
              </Button>
            </label>
          </div>
        </TabsContent>

        <TabsContent value="urls">
          <div className="border border-border p-6">
            <Label className="overline">Website URL</Label>
            <p className="text-sm text-muted-foreground mb-4">Enter a URL to scrape content from</p>
            <div className="flex gap-2">
              <Input
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder="https://example.com/page"
                className="rounded-none"
                data-testid="url-input"
              />
              <Button onClick={handleAddUrl} disabled={!urlInput.trim()} data-testid="add-url-btn">
                <Plus className="w-4 h-4 mr-2" /> Add URL
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="text">
          <div className="border border-border p-6">
            <Label className="overline">Plain Text</Label>
            <p className="text-sm text-muted-foreground mb-4">Paste text content directly</p>
            <Textarea
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              placeholder="Enter your content here..."
              className="rounded-none min-h-[150px] mb-4"
              data-testid="text-input"
            />
            <Button onClick={handleAddText} disabled={!textInput.trim()} data-testid="add-text-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Text
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="qa">
          <div className="border border-border p-6">
            <Label className="overline">Question & Answer Pairs</Label>
            <p className="text-sm text-muted-foreground mb-4">Add specific Q&A pairs for better accuracy</p>
            <Dialog open={qaOpen} onOpenChange={setQaOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="add-qa-btn">
                  <Plus className="w-4 h-4 mr-2" /> Add Q&A
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none">
                <DialogHeader>
                  <DialogTitle>Add Q&A Pair</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label className="overline">Question</Label>
                    <Input
                      value={qaQuestion}
                      onChange={(e) => setQaQuestion(e.target.value)}
                      placeholder="What is your return policy?"
                      className="rounded-none"
                      data-testid="qa-question-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="overline">Answer</Label>
                    <Textarea
                      value={qaAnswer}
                      onChange={(e) => setQaAnswer(e.target.value)}
                      placeholder="We offer a 30-day return policy..."
                      className="rounded-none min-h-[100px]"
                      data-testid="qa-answer-input"
                    />
                  </div>
                  <Button onClick={handleAddQA} className="w-full" data-testid="submit-qa-btn">
                    Add Q&A
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </TabsContent>
      </Tabs>

      {/* Sources List */}
      <div className="space-y-4">
        <h2 className="font-semibold">{LABEL_FOR_TAB[activeTab]} ({sources.filter(s => s.type === TYPE_FOR_TAB[activeTab]).length})</h2>
        {sources.filter(s => s.type === TYPE_FOR_TAB[activeTab]).length === 0 ? (
          <div className="text-center py-8 border border-dashed border-border">
            <p className="text-muted-foreground">No {LABEL_FOR_TAB[activeTab].toLowerCase()} added yet</p>
          </div>
        ) : (
          <div className="border border-border divide-y divide-border">
            {sources.filter(s => s.type === TYPE_FOR_TAB[activeTab]).map(source => (
              <div key={source.id} className="p-4 flex items-center justify-between" data-testid={`source-${source.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-muted flex items-center justify-center">
                    {getSourceIcon(source.type)}
                  </div>
                  <div>
                    {source.type === "qa" ? (
                      <>
                        <p className="font-medium text-sm break-words whitespace-pre-wrap">
                          Q: {source.metadata?.question}
                        </p>
                        <p className="text-sm text-muted-foreground break-words whitespace-pre-wrap mt-1">
                          A: {source.metadata?.answer || (source.content || "").replace(/^Q:\s*[\s\S]*?\nA:\s*/, "")}
                        </p>
                      </>
                    ) : (
                      <p className="font-medium text-sm break-words">
                        {source.type === "file" ? source.metadata?.filename :
                         source.type === "url" ? source.content :
                         source.content.substring(0, 50) + "..."}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {source.type.toUpperCase()} • {source.status}
                      {source.vector_count > 0 && ` • ${source.vector_count} vectors`}
                    </p>
                    {piiTotal(source) > 0 && (
                      <div className="mt-2 flex items-start gap-2 text-xs" style={{ color: "hsl(var(--warning-yellow))" }}>
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <p className="font-medium">
                            Contains personal data: {piiSummary(source)}
                          </p>
                          <p className="text-muted-foreground mt-0.5">
                            This text is sent to your AI provider when relevant. Business contact details you want the bot to share can be added to the privacy allowlist in Settings.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {piiTotal(source) > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setRedactTarget(source)}
                      className="h-8"
                      data-testid={`mask-pii-${source.id}`}
                    >
                      Mask personal data
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDeleteSource(source.id)}
                    className="h-8 w-8 text-muted-foreground hover:text-[hsl(var(--signal-red))]"
                    data-testid={`delete-source-${source.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* BC-009: confirm masking personal data inside a source */}
      <AlertDialog open={!!redactTarget} onOpenChange={(open) => { if (!open) setRedactTarget(null); }}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>Mask personal data?</AlertDialogTitle>
            <AlertDialogDescription>
              This will replace the personal data found in this source with markers such as [IC_1], permanently. This cannot be undone.
              {redactTarget && piiTotal(redactTarget) > 0 && ` Detected: ${piiSummary(redactTarget)}.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={redacting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleRedactSource(); }}
              disabled={redacting}
              className="bg-[hsl(var(--signal-red))] hover:bg-[hsl(var(--signal-red))]/90 text-white"
              data-testid="confirm-mask-btn"
            >
              {redacting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {redacting ? "Masking..." : "Mask personal data"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
